import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { useGetGemsAuditLogsQuery } from "../../../../redux/apiSlice";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { SubHeader } from "../../Common/layout/SubHeader";
import { formatDate } from "../../../util/Helpers";
import { ViewIcon } from "../../../util/Icons";

const todayStr = new Date().toISOString().split("T")[0];
const PAGE_SIZE = 10;
const getAuditLogTimestamp = (log) => {
    const candidates = [log?.created, log?.updated];

    for (const candidate of candidates) {
        if (!candidate) continue;
        const timestamp = new Date(candidate).getTime();
        if (Number.isFinite(timestamp)) return timestamp;
    }

    return 0;
};

const GemsAuditLogs = () => {
    const { userGroup } = useParams();

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: todayStr,
    });
    const [page, setPage] = useState(1);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    const args = useMemo(
        () => ({
            userGroup,
            startDate: filters.startDate || "",
            endDate: filters.endDate || "",
            limit: 1000,
        }),
        [userGroup, filters.startDate, filters.endDate]
    );

    const { data: auditLogs, isFetching } = useGetGemsAuditLogsQuery(args);
    const logs = useMemo(() => {
        const payload = auditLogs?.data ?? auditLogs ?? null;
        const rows = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.rows)
                ? payload.rows
                : Array.isArray(payload?.data)
                    ? payload.data
                    : Array.isArray(auditLogs?.rows)
                        ? auditLogs.rows
                        : [];

        return rows.slice().sort((a, b) => getAuditLogTimestamp(b) - getAuditLogTimestamp(a));
    }, [auditLogs]);
    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(logs.length / PAGE_SIZE)),
        [logs.length]
    );
    const pagedLogs = useMemo(() => {
        const start = (Math.max(1, page) - 1) * PAGE_SIZE;
        return logs.slice(start, start + PAGE_SIZE);
    }, [logs, page]);

    const onFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        setPage(1);
    }, [filters.startDate, filters.endDate, userGroup]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const renderAuditValue = (value, depth = 0) => {
        if (value === null || value === undefined) return <span className="text-muted">-</span>;
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            return <span>{String(value)}</span>;
        }

        if (Array.isArray(value)) {
            if (depth >= 2) return <span className="text-muted">…</span>;
            if (!value.length) return <span className="text-muted">Empty</span>;

            return (
                <div className="d-flex flex-column">
                    {value.slice(0, 12).map((v, idx) => (
                        <div key={idx} className="py-1">
                            {renderAuditValue(v, depth + 1)}
                        </div>
                    ))}
                    {value.length > 12 && <div className="text-muted py-1">+{value.length - 12} more</div>}
                </div>
            );
        }

        if (typeof value === "object") {
            if (depth >= 2) return <span className="text-muted">…</span>;

            const entries = Object.entries(value);
            if (!entries.length) return <span className="text-muted">Empty</span>;

            return (
                <div className="border rounded bg-light p-3">
                    {entries.slice(0, 24).map(([k, v]) => (
                        <div key={k} className="d-flex py-1 border-bottom" style={{ borderColor: "#EBEDF3" }}>
                            <div className="text-muted font-size-sm" style={{ minWidth: 140 }}>
                                {k}
                            </div>
                            <div className="flex-grow-1 pl-3">{renderAuditValue(v, depth + 1)}</div>
                        </div>
                    ))}
                    {entries.length > 24 && (
                        <div className="text-muted font-size-sm pt-2">+{entries.length - 24} more fields</div>
                    )}
                </div>
            );
        }

        return <span className="text-muted">-</span>;
    };

    return (
        <>
            {userGroup && (
                <SubHeader>
                    <SubHeader.Nav>
                        <SubHeader.NavItem label={"Overview"} to={`/gems/${userGroup}/overview`} />
                        <SubHeader.NavItem label={"Accruals"} to={`/gems/${userGroup}/overview/accruals`} />
                        <SubHeader.NavItem label={"Redemptions"} to={`/gems/${userGroup}/overview/redemptions`} />
                        <SubHeader.NavItem label={"Campaigns"} to={`/gems/${userGroup}/overview/campaigns`} />
                        <SubHeader.NavItem label={"Reward attempts"} to={`/gems/${userGroup}/overview/reward-attempts`} />
                        <SubHeader.NavItem label={"Audit logs"} to={`/gems/${userGroup}/overview/audit-logs`} />
                    </SubHeader.Nav>
                </SubHeader>
            )}
            <div className="py-4 px-6">
                <div className="mb-4">
                    <div>
                        <h2 className="font-weight-bolder mb-1">Audit logs</h2>
                        <span className="text-muted">Track Gems report actions and changes made by admins.</span>
                    </div>
                    <div className="d-flex flex-wrap mt-4">
                        <div className="mr-3 mb-2">
                            <label className="text-muted font-size-sm mb-1">Start date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="startDate"
                                value={filters.startDate}
                                onChange={onFilterChange}
                            />
                        </div>
                        <div className="mb-2">
                            <label className="text-muted font-size-sm mb-1">End date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="endDate"
                                value={filters.endDate}
                                onChange={onFilterChange}
                            />
                        </div>
                    </div>
                </div>

                <Card>
                    <CardHeader title="Audit logs" />
                    <CardBody>
                        <div className="table-responsive">
                            <table className="table table-sm table-head-custom">
                                <thead>
                                    <tr>
                                        <th>Created</th>
                                        <th>Admin</th>
                                        <th>Action</th>
                                        <th>Entity</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isFetching ? (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-5">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : pagedLogs.length ? (
                                        pagedLogs.map((l) => (
                                            <tr key={l.id}>
                                                <td>{formatDate(l.created)}</td>
                                                <td className="text-muted" style={{ minWidth: 240 }}>
                                                    {l.adminName || "-"}
                                                </td>
                                                <td style={{ minWidth: 220 }}>{l.action || "-"}</td>
                                                <td style={{ minWidth: 240 }}>
                                                    <div className="font-weight-bolder">{l.entityType || "-"}</div>
                                                    <div className="text-muted">{l.entityId || "-"}</div>
                                                </td>
                                                <td style={{ minWidth: 120 }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-icon btn-light btn-sm"
                                                        onClick={() => {
                                                            setSelectedLog(l);
                                                            setShowDetailsModal(true);
                                                        }}
                                                    >
                                                        <ViewIcon size={"18px"} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-5">
                                                No audit logs for selected filters
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {!isFetching && logs.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div className="text-muted font-size-sm">
                                    Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, logs.length)} of {logs.length}
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-light mr-2"
                                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                        disabled={page <= 1}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                        disabled={page >= totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>

            <Modal
                show={showDetailsModal}
                onHide={() => setShowDetailsModal(false)}
                size="lg"
                aria-labelledby="gems-audit-log-details"
            >
                <Modal.Header>
                    <Modal.Title id="gems-audit-log-details">
                        <span className="svg-icon svg-icon-lg svg-icon-primary mr-3">
                            <ViewIcon size={"20px"} />
                        </span>
                        Audit log details
                    </Modal.Title>
                    <button type="button" className="close" onClick={() => setShowDetailsModal(false)}>
                        <span aria-hidden="true" className="d-block">
                            ×
                        </span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="p-6" style={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}>
                    <div className="mb-6">
                        <div className="d-flex flex-wrap">
                            <div className="mr-8 mb-3">
                                <div className="text-muted font-size-sm">Created</div>
                                <div className="font-weight-bolder">{formatDate(selectedLog?.created)}</div>
                            </div>
                            <div className="mr-8 mb-3">
                                <div className="text-muted font-size-sm">Admin</div>
                                <div className="font-weight-bolder">{selectedLog?.adminName || "-"}</div>
                            </div>
                            <div className="mr-8 mb-3">
                                <div className="text-muted font-size-sm">Action</div>
                                <div className="font-weight-bolder">{selectedLog?.action || "-"}</div>
                            </div>
                            <div className="mb-3">
                                <div className="text-muted font-size-sm">Entity</div>
                                <div className="font-weight-bolder">{selectedLog?.entityType || "-"}</div>
                                <div className="text-muted">{selectedLog?.entityId || "-"}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="font-weight-bolder mb-2">Before</div>
                        {renderAuditValue(selectedLog?.beforeValue)}
                    </div>
                    <div className="mb-6">
                        <div className="font-weight-bolder mb-2">After</div>
                        {renderAuditValue(selectedLog?.afterValue)}
                    </div>
                    <div>
                        <div className="font-weight-bolder mb-2">Meta</div>
                        {renderAuditValue(selectedLog?.meta)}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default GemsAuditLogs;
